import React, { ReactElement, useState, useContext, useMemo, useCallback } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Badge } from "@chakra-ui/react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import { Link } from "react-router-dom";
import { Tooltip, IconButton } from "@mui/material";
import { AiOutlineFileSearch } from "react-icons/ai";
import { BsPersonCheck } from "react-icons/bs";
import { MdOutlineContentCopy } from "react-icons/md";
import ReceiverResendKYCConfirmModal from "./ReceiverResendKYCConfirmModal";
import SweetAlert2 from "react-sweetalert2";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ProfileContext } from "../../../../store/ProfileProvider";
import AgGridPagination from "../../shared/AgGridPagination";

dayjs.extend(utc);
dayjs.extend(timezone);

ModuleRegistry.registerModules([AllCommunityModule]);

type Merchant = {
  id: string;
  name: string;
};

type Subaccount = {
  id: string;
  name: string;
};

type RowData = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  reference: string;
  status: string;
  kyc_status: string;
  created_at: string;
  merchant: Merchant;
  subaccount: Subaccount;
};

const ReceiverList: React.FC<{
  isLoading: boolean;
  tableData: RowData[];
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: any;
  currentLastKey: string;
  triggerReloadList(): void;
}> = ({
  isLoading,
  tableData,
  page,
  pageSize,
  rowCount,
  setPaginationModel,
  currentLastKey,
  triggerReloadList,
}) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const [isOpenResendKYCConfirmModal, setIsOpenResendKYCConfirmModal] =
    useState<boolean>(false);
  const [receiverDetailForResendKYC, setReceiverDetailForResendKYC] =
    useState(null);
  const [swalProps, setSwalProps] = useState<any>({});
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  interface CustomerStatusItems {
    status: string;
    value: string;
    display: ReactElement;
  }

  const statusClasses = "status";

  const statusList: CustomerStatusItems[] = [
    {
      status: "creating",
      value: "creating",
      display: (
        <Badge className={`${statusClasses} status-initializing`}>Creating</Badge>
      ),
    },
    {
      status: "pending",
      value: "pending",
      display: (
        <Badge className={`${statusClasses} status-pending`}>Pending</Badge>
      ),
    },
    {
      status: "completed",
      value: "completed",
      display: (
        <Badge className={`${statusClasses} status-completed`}>Completed</Badge>
      ),
    },
    {
      status: "active",
      value: "active",
      display: (
        <Badge className={`${statusClasses} status-active`}>Active</Badge>
      ),
    },
    {
      status: "inactive",
      value: "inactive",
      display: (
        <Badge className={`${statusClasses} status-inactive`}>Inactive</Badge>
      ),
    },
    {
      status: "failed",
      value: "failed",
      display: (
        <Badge className={`${statusClasses} status-failed`}>Failed</Badge>
      ),
    },
  ];

  const kycStatusList: CustomerStatusItems[] = [
    {
      status: "creating",
      value: "creating",
      display: (
        <Badge className={`${statusClasses} status-initializing`}>Creating</Badge>
      ),
    },
    {
      status: "pending",
      value: "pending",
      display: (
        <Badge className={`${statusClasses} status-pending`}>Pending</Badge>
      ),
    },
    {
      status: "completed",
      value: "completed",
      display: (
        <Badge className={`${statusClasses} status-completed`}>Completed</Badge>
      ),
    },
    {
      status: "active",
      value: "active",
      display: (
        <Badge className={`${statusClasses} status-active`}>Active</Badge>
      ),
    },
    {
      status: "inactive",
      value: "inactive",
      display: (
        <Badge className={`${statusClasses} status-inactive`}>Inactive</Badge>
      ),
    },
    {
      status: "failed",
      value: "failed",
      display: (
        <Badge className={`${statusClasses} status-failed`}>Failed</Badge>
      ),
    },
  ];

  const handleClickCopy = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch (err) {
      console.error("Failed to copy payout ID: ", err);
    }
  };

  const formatDate = (timestamp: string) => {
    return dayjs(Number(timestamp))
      .tz(profileTimeZone)
      .format("DD MMM YY - hh:mm A");
  };

  const handleResendKYC = (receiver: any) => {
    setReceiverDetailForResendKYC(receiver);
    setIsOpenResendKYCConfirmModal(true);
  };

  const closeResendKYCConfirmModal = () => {
    setReceiverDetailForResendKYC(null);
    setIsOpenResendKYCConfirmModal(false);
  };

  const CustomLoadingOverlay = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <CircularProgress size={40} />
    </div>
  );

  // AG Grid cell renderers
  const CopyIdRenderer = (params: ICellRendererParams) => (
    <Tooltip title="Copy Receiver ID" arrow placement="top">
      <div>
        <IconButton
          id={"button-copy-receiverid-" + params.data?.id}
          onClick={() => handleClickCopy(params.data.id)}
          size="small"
        >
          <MdOutlineContentCopy className="h-5 w-5 text-navy-800" />
        </IconButton>
      </div>
    </Tooltip>
  );

  const CreatedDateRenderer = (params: ICellRendererParams) => (
    <>{formatDate(params.data.created_at)}</>
  );

  const SubaccountRenderer = (params: ICellRendererParams) => (
    <>{params.data.subaccount?.name}</>
  );

  const StatusRenderer = (params: ICellRendererParams) => {
    const status = params.data.status;
    const statusItem = statusList.find((item) => item.status === status);
    return <>{statusItem ? statusItem.display : null}</>;
  };

  const KycStatusRenderer = (params: ICellRendererParams) => {
    const kycStatus = params.data.kyc_status;
    const statusItem = kycStatusList.find((item) => item.status === kycStatus);
    return <>{statusItem ? statusItem.display : null}</>;
  };

  const ActionsRenderer = (params: ICellRendererParams) => (
    <div className="flex items-center justify-end">
      {(params.data?.kyc_status === "pending" ||
        params.data?.kyc_status === "failed") &&
        userRole === "superadmin" && (
          <Tooltip title="Resend KYC Link" arrow placement="top">
            <div>
              <IconButton
                id={"button-resend-kyc-" + params.data?.id}
                onClick={() => handleResendKYC(params.data)}
                size="small"
              >
                <BsPersonCheck className="h-6 w-6 text-navy-700 opacity-90" />
              </IconButton>
            </div>
          </Tooltip>
        )}
      <Tooltip title="View More Details" arrow placement="top">
        <div>
          <IconButton
            component={Link}
            to={`/${userRole}/receiver/${params.data.id}`}
            id={"button-detail-" + params.data?.id}
            size="small"
          >
            <AiOutlineFileSearch className="h-5 w-5 text-navy-700" />
          </IconButton>
        </div>
      </Tooltip>
    </div>
  );

  const columns: ColDef[] = [
    {
      field: "id",
      headerName: "",
      width: 50,
      sortable: false,
      resizable: false,
      cellRenderer: CopyIdRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "created_at",
      headerName: "Created Date",
      sortable: false,
      minWidth: 140,
      flex: 1,
      cellRenderer: CreatedDateRenderer,
    },
    {
      field: "subaccount",
      headerName: "Subaccount",
      sortable: false,
      minWidth: 100,
      flex: 1,
      cellRenderer: SubaccountRenderer,
    },
    {
      field: "first_name",
      headerName: "First Name",
      sortable: false,
      minWidth: 100,
      flex: 1,
    },
    {
      field: "last_name",
      headerName: "Last Name",
      sortable: false,
      minWidth: 100,
      flex: 1,
    },
    {
      field: "email",
      headerName: "Email",
      sortable: false,
      minWidth: 140,
      flex: 1.5,
    },
    {
      field: "status",
      headerName: "Status",
      sortable: false,
      width: 120,
      cellRenderer: StatusRenderer,
      cellClass: "ag-cell-status",
    },
    {
      field: "kyc_status",
      headerName: "KYC Status",
      sortable: false,
      width: 120,
      cellRenderer: KycStatusRenderer,
      cellClass: "ag-cell-status",
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      width: 100,
      resizable: false,
      cellRenderer: ActionsRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
    },
  ];

  const filterColumns = useCallback(() => {
    let rebuildColumns = [...columns];
    if (userRole !== "superadmin") {
      const excludeFields = ["subaccount"];
      rebuildColumns = rebuildColumns.filter(
        (column) => !excludeFields.includes(column.field!)
      );
    }
    return rebuildColumns;
  }, [userRole]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  const hidePagination = currentLastKey === "" && page === 0;

  return (
    <div>
      {isOpenResendKYCConfirmModal && (
        <ReceiverResendKYCConfirmModal
          setSwalProps={setSwalProps}
          triggerReload={triggerReloadList}
          isOpen={isOpenResendKYCConfirmModal}
          receiverToResendKYC={receiverDetailForResendKYC}
          onClose={closeResendKYCConfirmModal}
        />
      )}

      <Box className="card mt-3 w-full">
        <div style={{ width: "100%" }}>
          <AgGridReact
            theme={themeAlpine}
            rowData={isLoading ? [] : tableData}
            columnDefs={filterColumns()}
            defaultColDef={defaultColDef}
            getRowId={(params) => params.data.id}
            rowHeight={48}
            headerHeight={44}
            domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
            suppressPaginationPanel={true}
            loading={isLoading}
            loadingOverlayComponent={CustomLoadingOverlay}
            overlayNoRowsTemplate="No data available"
          />
        </div>

        <AgGridPagination
          page={page}
          pageSize={pageSize}
          rowCount={rowCount}
          onPaginationChange={setPaginationModel}
          hidden={hidePagination}
        />
      </Box>

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </div>
  );
};

export default ReceiverList;
